import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const monthName = (m) => ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m];

export default function ProjectCalendar() {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [events, setEvents] = useState([]);
  const range = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const last = new Date(cursor.y, cursor.m + 1, 0);
    return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10),
             firstDow: first.getDay(), days: last.getDate() };
  }, [cursor]);
  useEffect(() => {
    api.get("/projects/calendar", { params: { from_date: range.from, to_date: range.to } }).then((r) => setEvents(r.data.events || []));
  }, [range.from, range.to]);
  const eventsByDay = useMemo(() => {
    const m = {};
    events.forEach((e) => { (m[e.date] = m[e.date] || []).push(e); });
    return m;
  }, [events]);

  const prev = () => setCursor(cursor.m === 0 ? { y: cursor.y - 1, m: 11 } : { y: cursor.y, m: cursor.m - 1 });
  const next = () => setCursor(cursor.m === 11 ? { y: cursor.y + 1, m: 0 } : { y: cursor.y, m: cursor.m + 1 });

  const cells = [];
  for (let i = 0; i < range.firstDow; i++) cells.push(null);
  for (let d = 1; d <= range.days; d++) cells.push(d);

  return (
    <div className="space-y-4">
      <PageHeader title="Project Calendar" description="Project start/end, milestones and task deadlines." />
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-lg font-semibold">{monthName(cursor.m)} {cursor.y}</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={prev}>Prev</Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; })}>Today</Button>
            <Button variant="outline" size="sm" onClick={next}>Next</Button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-xs">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="text-center py-1 opacity-60 uppercase">{d}</div>
          ))}
          {cells.map((day, idx) => {
            if (day === null) return <div key={`e-${idx}`} className="min-h-[80px] bg-slate-50 dark:bg-slate-900/40 rounded" />;
            const dateStr = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            const dayEvents = eventsByDay[dateStr] || [];
            return (
              <div key={dateStr} className="min-h-[80px] border rounded p-1 flex flex-col gap-0.5">
                <div className="text-[10px] opacity-60">{day}</div>
                {dayEvents.slice(0, 3).map((e, i) => (
                  <div key={i} className="text-[10px] rounded px-1 py-0.5 truncate cursor-pointer text-white"
                       style={{ backgroundColor: e.color }}
                       onClick={() => e.project_id && navigate(`/projects/${e.project_id}`)}
                       title={e.title}>{e.title}</div>
                ))}
                {dayEvents.length > 3 && <div className="text-[9px] opacity-60">+{dayEvents.length - 3} more</div>}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
